export interface Sim {
  esimId: string;
  name: string;
  eSimState: SimStates;
  iccid: number;
  activationCode: string;
  imsiId: number;
  active: boolean;
  isDeleted: boolean;
  country: string;
  subscriptions: SimSubscription[];
}

export interface SimSubscription {
  _id: string;
  name: string;
  coverage: PlanType;
  stripeProductId: string;
  currency: string;
  productId: string;
  customerId: string;
  reFelling: boolean;
  country: string;
  validity: number;
  productCategory: string;
  data: string;
  transactionId: string;
  expiryDate?: string;
  activationDate?: string;
  SMDPSubscriptionId: string;
  cycleUnits: string;
  cycle: number;
  planAUtoRefilled: boolean;
  isActivationProcessStatus: boolean;
  isActive: boolean;
  isDeleted: boolean;
  status: SubscriptionStatus;
  iccid: string;
  isAUtoRefillOn: boolean;
  subscriberId: string;
  price: number;
  eSimState: SimStates;
  defaultPrice: {
    price: number;
    currency: string;
  };
  convertedPrice: {
    price: number;
    currency: string;
  };
  newEsim: boolean;
  purchaseDate: string | Date;
  shopifyId: number;
  earnedPoints: number;
  rewardPointsRedeemed: number;
  rewardCashValueRedeemed: number;
  paymentMethod: PaymentMethods;
  dataUsage: number;
  dataUsageAllowanceInGb: number;
  preferredImsiId: number;
  couponAmount: number;
  platform: string;
  flag: string;
  isCountry: boolean;
  imsi: string;
  isTopup: boolean;
  regionType: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  planId: string;
  planData: {
    countryName: string;
    iso3code: string;
    supportedCountries: string[];
    supportedRegion: string;
    name: string;
    flagUrl: string;
    imageUrl: string;
  };
  activationCode: string;
  couponCode?: string;
  topupSubscriptionId?: string;
  dataUsageAllowanceType?: string;
  expiredDate?: string;
  refundDate?: string;
  fupDescription?: string;
  autoTopupEnabled?: boolean;
  autoTopupPaymentMethodId?: string;
  autoTopupRetry?: boolean;
  autoTopupError?: boolean;
  planAutoTopUpEnabled?: boolean;
  orderId?: string;
}

export enum PaymentMethods {
  Card = 'card',
  Apple = 'apple',
  GooglePay = 'google',
}

export enum PlanType {
  Country = 'COUNTRY',
  Region = 'REGION',
}

export enum SubscriptionStatus {
  Active = 'active',
  InActive = 'upcoming',
  Expired = 'expired',
  Archived = 'refunded',
  Terminated = 'terminated',
}

/**
 * AVAILABLE -> The Profile is available in the inventory of the SM-DP+ Inactive
 * ALLOCATED -> The Profile is reserved for downloading without being linked to an EID. Inactive
 * LINKED -> The Profile is reserved for downloading and is linked to an EID. Inactive
 * CONFIRMED -> The Profile is reserved for downloading (linked or not linked to an EID) with Matching ID and Confirmation Code if required. Inactive
 * RELEASED -> The Profile is ready for download and installation after Network Configuration by the Operator (e.g.: HLR Registration). Inactive
 * DOWNLOADED -> The Bound Profile was delivered to the LPA. Inactive
 * INSTALLED -> The Profile was successfully installed on the eUICC. Inactive
 * ENABLED -> The last Notification received from the eUICC was an enable Notification. Active
 * DISABLED -> The last Notification received from the eUICC was a disable Notification. Inactive
 * ERROR -> The Profile has not been installed because of one of the following error cases: Inactive
 * - Confirmation Code Retry Limit exceeded
 * - Download Retry Limit exceeded
 * - End User Rejection
 * - Permanent error during download and installation
 * UNAVAILABLE -> The Profile cannot be reused anymore by the SM-DP+. Deleted
 * DELETED -> The last Notification received from the eUICC was a delete Notification. Deleted
 */

export enum SimStates {
  Available = 'AVAILABLE',
  Allocated = 'ALLOCATED',
  Linked = 'LINKED',
  Confirmed = 'CONFIRMED',
  Released = 'RELEASED',
  Downloaded = 'DOWNLOADED',
  Installed = 'INSTALLED',
  Enabled = 'ENABLED',
  Disabled = 'DISABLED',
  Error = 'ERROR',
  UnAvailable = 'UNAVAILABLE',
  Deleted = 'DELETED',
}

export enum InternalSimStates {
  Deleted,
  Active,
  InActive,
}

export const SimStatesMapping: Record<SimStates, InternalSimStates> = {
  [SimStates.Available]: InternalSimStates.InActive,
  [SimStates.Allocated]: InternalSimStates.InActive,
  [SimStates.Linked]: InternalSimStates.InActive,
  [SimStates.Confirmed]: InternalSimStates.InActive,
  [SimStates.Released]: InternalSimStates.InActive,
  [SimStates.Downloaded]: InternalSimStates.InActive,
  [SimStates.Installed]: InternalSimStates.InActive,
  [SimStates.Enabled]: InternalSimStates.Active,
  [SimStates.Disabled]: InternalSimStates.Active,
  [SimStates.Error]: InternalSimStates.InActive,
  [SimStates.UnAvailable]: InternalSimStates.Deleted,
  [SimStates.Deleted]: InternalSimStates.Deleted,
};

export const StateNames: Record<InternalSimStates, string> = {
  [InternalSimStates.Active]: 'Active',
  [InternalSimStates.Deleted]: 'Deleted',
  [InternalSimStates.InActive]: 'Inactive',
};

export const PlanStateNames: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.Active]: 'Active',
  [SubscriptionStatus.Expired]: 'Expired',
  [SubscriptionStatus.Archived]: 'Refunded',
  [SubscriptionStatus.InActive]: 'Inactive',
  [SubscriptionStatus.Terminated]: 'Terminated',
};

// Re-export types from other modules for convenience
export type { CheckoutData } from '../core/services/storage/storage.service';
export type { PlanDetails } from '../utils/plan-details-from-sim-subscription';
